import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CallIcon from "@mui/icons-material/Call";
import MailIcon from "@mui/icons-material/Mail";
import {
  Box,
  Card,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../components/Custom3dotString";
import CustomButton from "../../components/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomModal from "../../components/CustomModal/CustomModal";
import CustomTextField from "../../components/CustomTextField";
import CustomUpload from "../../components/CustomUpload";
import Select from "../../components/Select/Select";
import {
  getClinicUsers,
  registerClinicUser,
} from "../../redux/reducers/clinicSlice";
import {
  createUserPetByUserId,
  getPetBreedsByType,
  getPetTypes,
} from "../../redux/reducers/petSlice";
import { AppColors } from "../../util/AppColors";
import { w25h25BlueIcon } from "../../util/CommonIconStyle";
import {
  bloodGroups,
  genderList,
  petOwnershipTypes,
  reproductiveStatusFemaleList,
  reproductiveStatusMaleList,
  reproductiveStatusUnknownList,
  temperaments,
} from "../../util/dropList";
import {
  generate4DigitRandomNumber,
  reduceYearsAndMonthsToStartOfMonth,
} from "../../util/function";

const initValues = {
  petName: "",
  parentName: "",
  address: "",
  userMobile: "",
  email: "",
  password: "",
  rfid: "",
  weight: "",
  breed: null,
  petType: null,
  color: null,
  gender: null,
  reproductiveStatus: null,
  ageType: "Year",
  dob: null,
  image: null,
  bloodGroup: null,
  temperament: null,
  ownershipType: null,
};

const initErrors = {
  petName: "",
  dob: "",
  breed: "",
  gender: "",
  address: "",
  reproductiveStatus: "",
  weight: "",
  parentName: "",
  userMobile: "",
  email: "",
  password: "",
  color: "",
  petType: "",
  rfId: "",
  bloodGroup: "",
  temperament: "",
};

const requiredFields = [
  "petName",
  "dob",
  "breed",
  // "gender",
  // "reproductiveStatus",
  // "weight",
  "petType",
];

const CreateClinicPet = ({
  createModVisible,
  onCloseCreateMod,
  userDetails,
  setBookModVisible,
  setSelectPet,
  selectedStep = 0,
  showRegisterOnly = false,
}) => {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeStep, setActiveStep] = useState(userDetails ? 1 : 0);
  const [petValues, setPetValues] = useState(initValues);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errors, setErrors] = useState(initErrors);
  const [petColorList, setPetColorList] = useState([]);
  const [breedList, setBreedList] = useState([]);
  const [petTypeList, setPetTypeList] = useState([]);
  const { users } = useSelector((state) => state?.clinic);
  const petDetails = useSelector((state) => state?.pet?.petDetails);

  useEffect(() => {
    setSelectedUser(selectedStep === 0 ? null : userDetails);
    setActiveStep(selectedStep);
    // if (userDetails) {
    //   setSelectedUser(userDetails);
    // }
    // if (selectedStep) {
    //   setActiveStep(selectedStep);
    // }
  }, [userDetails, selectedStep]);

  useEffect(() => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    dispatch(getPetTypes()).then((res) => {
      if (res?.payload) {
        const reqList = [...(res?.payload ?? [])]
          ?.sort((a, b) =>
            (a?.speciesName ?? "")?.localeCompare(b?.speciesName ?? ""),
          )
          ?.map((sp) => ({
            name: sp?.speciesName,
            value: sp?.speciesId,
          }));
        setPetTypeList(reqList);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (searchValue?.length > 2) {
      dispatch(getClinicUsers(`?search=${searchValue}&itemSize=30&page=1`));
    }
  }, [searchValue]);

  const getFilteredUserList = () => {
    const reqList = users?.filter((usr) => {
      if (
        usr?.name?.toLowerCase().includes(searchValue?.toLowerCase()) ||
        usr?.email?.toLowerCase().includes(searchValue?.toLowerCase()) ||
        usr?.mobile?.toLowerCase().includes(searchValue?.toLowerCase())
      ) {
        return usr;
      }
    });
    return reqList ?? [];
  };

  const handleSelectedUser = (user) => {
    setSelectedUser(user);
    setActiveStep(2);
  };

  const handleInputChange = (e, field) => {
    const reqObj = { ...petValues, [field]: e?.target?.value };
    if (field === "ownershipType" && e?.target?.value === "Rescue") {
      reqObj.petName = `Stray-${generate4DigitRandomNumber()}`;
    }
    setPetValues(reqObj);
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const handleSelectChange = (value, field) => {
    setPetValues({ ...petValues, [field]: value });
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
    if (field === "petType") {
      dispatch(getPetBreedsByType(value?.value)).then((res) => {
        if (res?.payload) {
          const reqList = [...(res?.payload ?? [])]
            ?.sort((a, b) =>
              (a?.breedName ?? "")?.localeCompare(b?.breedName ?? ""),
            )
            ?.map((bd) => ({
              ...bd,
              name: bd?.breedName,
              value: bd?.breedName,
            }));
          setBreedList(reqList);
        }
      });
    }
  };

  const onUploadFile = (e) => {
    const reader = new FileReader();
    const file = e?.target?.files[0];
    reader.onloadend = () => {
      setPetValues({
        ...petValues,
        image: { file: e.target.files[0], previewUrl: reader.result },
      });
    };
    reader.readAsDataURL(file);
  };

  const validateParentFields = () => {
    const newErrors = {};
    if (!petValues?.parentName) newErrors.parentName = "This field is required";
    if (!petValues?.userMobile) newErrors.userMobile = "This field is required";
    if (petValues?.email?.length > 0 && !petValues?.email.includes("@")) {
      newErrors.email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFields = () => {
    let errObj = {};
    requiredFields?.forEach((key) => {
      const reqValue =
        typeof petValues?.[key] === "string"
          ? petValues?.[key]
          : petValues?.[key]?.value;
      if (reqValue?.length === 0 || !reqValue) {
        errObj = { ...errObj, [key]: "This field is required" };
      }
    });
    setErrors({ ...errors, ...errObj });
    return Object.keys(errObj).length > 0;
  };

  const handleNext = () => {
    if (selectedUser) return setActiveStep(activeStep + 1);
    if (!validateParentFields()) return;
    const data = new FormData();

    data.append("userName", petValues?.parentName);
    data.append("userMobile", petValues?.userMobile);
    if (petValues?.address?.length > 0)
      data.append("address", petValues?.address);
    if (petValues?.email?.length > 0) data.append("email", petValues?.email);
    if (petValues?.secondaryMobile?.length > 0) {
      data.append("secondaryMobile", petValues?.secondaryMobile);
    }

    dispatch(registerClinicUser(data)).then((res) => {
      if (res?.payload?.status === 201) {
        setSelectedUser(res?.payload?.data);
        setActiveStep(activeStep + 1);
      }
    });
  };

  const createPet = (book = false) => {
    if (validateFields()) return;
    const data = new FormData();
    if (petValues?.ownershipType) {
      data.append("ownershipType", petValues?.ownershipType);
    }
    data.append("petName", petValues?.petName);
    data.append("petType", petValues?.petType?.name);
    data.append("breed", petValues?.breed?.value);
    data.append("weight", petValues?.weight ?? "");
    data.append("reproductiveStatus", petValues?.reproductiveStatus);
    data.append("gender", petValues?.gender);
    data.append(
      "dob",
      reduceYearsAndMonthsToStartOfMonth({
        years: petValues?.ageType === "Year" ? Number(petValues?.dob) : 0,
        months: petValues?.ageType === "Month" ? Number(petValues?.dob) : 0,
      }),
    );
    data.append("color", petValues?.color ?? "");
    if (petValues?.image?.file) data.append("petImage", petValues?.image?.file);
    if (petValues?.rfid?.length > 0) data.append("rfid", petValues?.rfid);
    if (petValues?.bloodGroup?.value?.length > 0)
      data.append("bloodGroup", petValues?.bloodGroup?.value);
    if (petValues?.temperament?.value?.length > 0)
      data.append("temperament", petValues?.temperament?.value);

    dispatch(
      createUserPetByUserId({
        userId:
          selectedUser?.id ??
          selectedUser?.userId ??
          userDetails?.id ??
          petDetails?.userId,
        data,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseMod(
          "petCreated",
          {
            ...(res?.payload ?? {}),
            userName:
              selectedUser?.name ??
              userDetails?.userName ??
              petDetails?.userName,
          } ?? null,
        );
        setSelectedUser(null);
        if (book) {
          const pet = res?.payload;
          setSelectPet({
            breed: pet?.breed,
            clinicId: pet?.clinicId,
            dob: pet?.dob,
            gender: pet?.gender,
            medicalHistoryUrl: null,
            oldPatientId: null,
            paymentId: null,
            petId: pet?.petId,
            petName: pet?.petName,
            status: pet?.status === "Y",
            userId: pet?.userId,
            userName:
              selectedUser?.name ??
              userDetails?.userName ??
              petDetails?.userName,
          });
          setBookModVisible(true);
        }
      }
    });
  };

  const onCloseMod = (str, petData) => {
    onCloseCreateMod(str ?? null, petData ?? null);
    setActiveStep(userDetails ? 1 : 0);
    setSelectedUser(userDetails ? userDetails : null);
    setSearchValue("");
    setPetValues(initValues);
    setErrors(initErrors);
  };

  return (
    <CustomModal
      open={createModVisible}
      onClose={onCloseMod}
      header={`${activeStep === 0 ? "Customer" : "Pet"} Registration`}
      rightModal
      modalWidth={30}
    >
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={activeStep}>
          {["1", "2"].map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>Step {label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {activeStep === 0 ? (
          <div className="scroll-70vh ph20 mt10">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  fullWidth
                  search
                  value={searchValue}
                  placeholder="Search Parent"
                  handleChange={(e) => setSearchValue(e?.target?.value)}
                  backgroundColor={AppColors.gray6}
                />
              </Grid>

              {searchValue?.length > 2 && (
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  {getFilteredUserList()?.length > 0 &&
                    getFilteredUserList()?.map((usr) => {
                      return (
                        <Grid
                          container
                          spacing={1}
                          key={usr?.id}
                          className="cursor"
                          onClick={() => handleSelectedUser(usr)}
                        >
                          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <Card
                                  sx={{
                                    borderRadius: 1,
                                    padding: 2,
                                    borderTopColor: AppColors.appPrimary,
                                  }}
                                  className="CustomCard-back-appointment"
                                >
                                  <div className="maint">
                                    <div className="flex-row">
                                      <Grid item>
                                        <div className="flex-row">
                                          <div className="flex-start">
                                            <div className="flex-column">
                                              <div className="flex-row">
                                                <Typography className="mb10 font-bold fs14 capitalize">
                                                  {usr?.name}
                                                </Typography>
                                              </div>
                                              <Typography className="mb10 txt-regular card-gray-color fs12">
                                                {usr?.email}
                                              </Typography>
                                              <Typography className="mb10 txt-regular card-gray-color fs12">
                                                {usr?.mobile}
                                              </Typography>
                                            </div>
                                          </div>
                                        </div>
                                      </Grid>
                                    </div>
                                  </div>
                                </Card>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      );
                    })}
                </Grid>
              )}

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="gray13 fs14 txt-semi-bold flex-center">
                  (or)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <div className="mb20">
                  <CustomTextField
                    label="Parent Name"
                    name="parentName"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "parentName")}
                    value={petValues?.parentName}
                    error={!!errors.parentName}
                    helperText={errors.parentName}
                    required={true}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <div className="mb20">
                  <CustomTextField
                    label="Contact Number"
                    name="userMobile"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "userMobile")}
                    value={petValues?.userMobile}
                    error={!!errors.userMobile}
                    helperText={errors.userMobile}
                    required={true}
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mb20">
                  <CustomTextField
                    label="Email"
                    name="email"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "email")}
                    value={petValues?.email}
                    error={!!errors.email}
                    helperText={errors.email}
                    required={true}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mb20">
                  <CustomTextField
                    label="Secondary Phone Number"
                    name="secondaryMobile"
                    fullWidth
                    handleChange={(e) =>
                      handleInputChange(e, "secondaryMobile")
                    }
                    value={petValues?.secondaryMobile}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mb20">
                  <CustomTextField
                    label="Address"
                    name="address"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "address")}
                    value={petValues?.address}
                    labelTop
                    multiline
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        ) : (
          <div className="scroll-80vh ph20 mt10">
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                className="mb30"
              >
                <div className="dashed-card mv20 p20">
                  <div className="flex-row-ali-cen">
                    <AccountCircleOutlinedIcon sx={{ width: 25, height: 25 }} />
                    <div className="flex-center">
                      <Custom3dotString
                        str={
                          selectedUser?.name ??
                          userDetails?.userName ??
                          petDetails?.userName
                        }
                        className="font-bold fs12 flex-center ml10"
                        length={30}
                      />
                    </div>
                  </div>
                  {(
                    selectedUser?.email ??
                    userDetails?.userEmail ??
                    petDetails?.userEmail
                  )?.length > 0 ? (
                    <div className="flex-row-ali-cen mt10">
                      <MailIcon sx={w25h25BlueIcon} />
                      <div className="flex-center">
                        <Typography className="txt-regular card-gray-color fs14 ml10 ovrflw-wrp-anywre">
                          {selectedUser?.email ??
                            userDetails?.userEmail ??
                            petDetails?.userEmail}
                        </Typography>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex-row-ali-cen mt10">
                    <CallIcon sx={w25h25BlueIcon} />
                    <div className="flex-center">
                      <Typography className="txt-regular card-gray-color fs14 ml10">
                        {selectedUser?.mobile ??
                          userDetails?.userMobile ??
                          petDetails?.userMobile}
                      </Typography>
                    </div>
                  </div>
                </div>
                <CustomUpload
                  uploadText="Profile Picture"
                  onUploadFile={onUploadFile}
                  value={petValues?.image?.previewUrl}
                  profileImg
                  imageHeight={140}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 light-grey mbminus">
                  Ownership Type:
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mbminus">
                  <CustomCheckbox
                    radio
                    value={petValues?.ownershipType}
                    onChange={(e) => handleInputChange(e, "ownershipType")}
                    radios={petOwnershipTypes}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Name <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <CustomTextField
                    name="petName"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "petName")}
                    value={petValues?.petName}
                    error={!!errors.petName}
                    helperText={errors.petName}
                    label="Name "
                    required={true}
                  />
                </div>
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Pet Type <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <Select
                    list={petTypeList}
                    value={petValues?.petType}
                    handleChange={(e, value) =>
                      handleSelectChange(value, "petType")
                    }
                    name="petType"
                    search
                    error={!!errors.petType}
                    helperText={errors.petType}
                    label="Species * "
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Age in {petValues?.ageType}{" "}
                  <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <CustomTextField
                    name="dob"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "dob")}
                    value={petValues?.dob}
                    error={!!errors.dob}
                    helperText={errors.dob}
                    label={`Age in ${petValues?.ageType}`}
                    type="number"
                    required={true}
                  />
                </div>
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Age Type <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <Select
                    list={[
                      { name: "Month", value: "Month" },
                      { name: "Year", value: "Year" },
                    ]}
                    value={petValues?.ageType}
                    handleChange={(e) =>
                      handleSelectChange(e?.target?.value, "ageType")
                    }
                    name="ageType"
                    select
                    error={!!errors.ageType}
                    helperText={errors.ageType}
                    label="Age Type *"
                  />
                </div>
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Breed <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <Select
                    list={breedList}
                    value={petValues?.breed}
                    handleChange={(e, value) =>
                      handleSelectChange(value, "breed")
                    }
                    name="breed"
                    search
                    error={!!errors.breed}
                    helperText={errors.breed}
                    label="Breed * "
                  />
                </div>
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Color <span className="red-color">*</span>
                </div> */}
                <div className="mb20">
                  <CustomTextField
                    label="Color"
                    name="color"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "color")}
                    value={petValues?.color ?? ""}
                    error={!!errors.color}
                    helperText={errors.color}
                  />
                </div>
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Gender
                 
                </div> */}
                <div className="mb20">
                  <Select
                    list={genderList}
                    value={petValues?.gender}
                    handleChange={(e) =>
                      handleSelectChange(e?.target?.value, "gender")
                    }
                    select
                    error={!!errors.gender}
                    helperText={errors.gender}
                    label="Gender "
                  />
                </div>
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">
                  Reproductive Status

                </div> */}
                <div className="mb20">
                  <Select
                    list={
                      petValues?.gender?.toLowerCase() === "male"
                        ? reproductiveStatusMaleList
                        : petValues?.gender?.toLowerCase() === "female"
                          ? reproductiveStatusFemaleList
                          : reproductiveStatusUnknownList
                    }
                    value={petValues?.reproductiveStatus}
                    handleChange={(e) =>
                      handleSelectChange(e?.target?.value, "reproductiveStatus")
                    }
                    error={!!errors.reproductiveStatus}
                    helperText={errors.reproductiveStatus}
                    select
                    label="Reproductive Status"
                  />
                </div>
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                <div className="mb20">
                  <CustomTextField
                    name="weight"
                    value={petValues?.weight}
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "weight")}
                    label={"Weight"}
                    error={!!errors.weight}
                    helperText={errors.weight}
                    suffix={petValues?.weight !== "" ? "Kg" : ""}
                    // mandatoryField
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                <div className="mb20">
                  <CustomTextField
                    label="Microchip Number"
                    name="rfid"
                    fullWidth
                    handleChange={(e) => handleInputChange(e, "rfid")}
                    value={petValues?.rfid}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">Blood Group</div> */}
                <div className="mb20">
                  <Select
                    list={bloodGroups}
                    value={petValues?.bloodGroup}
                    handleChange={(e, value) =>
                      handleSelectChange(value, "bloodGroup")
                    }
                    name="bloodGroup"
                    error={!!errors.bloodGroup}
                    helperText={errors.bloodGroup}
                    label="Blood Group"
                    search
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                {/* <div className="txt-mont fs14">Temperament</div> */}
                <div className="mb20">
                  <Select
                    list={temperaments}
                    value={petValues?.temperament}
                    handleChange={(e, value) =>
                      handleSelectChange(value, "temperament")
                    }
                    name="temperament"
                    error={!!errors.temperament}
                    helperText={errors.temperament}
                    label="Temperament "
                    search
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="flex1-end">
                  {showRegisterOnly ? null : (
                    <div>
                      <CustomButton
                        text="Register + Book"
                        onClick={() => createPet(true)}
                        smallBtn
                      />
                    </div>
                  )}
                  <div className={showRegisterOnly ? "" : "ml10"}>
                    <CustomButton
                      text="Register"
                      onClick={() => createPet(false)}
                      smallBtn
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        )}

        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            {activeStep === 0 && (
              <div className="flex1-end">
                <div>
                  <CustomButton text="Next" smallBtn onClick={handleNext} />
                </div>
              </div>
            )}
          </Box>
        </Fragment>
      </Box>
    </CustomModal>
  );
};

export default CreateClinicPet;
